import lodash from 'lodash';
import { replace } from 'react-router-redux';

import Query from '@complion/query';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { mutateGraphQL, queryGraphQL } from 'common/util/query';
import {
    node, nodeWithArgs, toMutation, toQuery, transformIntoEnumerations
} from 'common/util/queryHelper';
const { QueryNodeRegistry, ValueSetRegistry, jsonHelper, validateQuery } = Query;

import {
    ALLOWED_SORTING_DIRECTIONS, ALLOWED_SORTING_TYPES
} from 'common/enums/folderSorting';

import BinderTemplateCsvLoader from './binderTemplateCsvLoader';

export const BINDER_TEMPLATE_INITIALIZE = 'BINDER_TEMPLATE_INITIALIZE';
export const BINDER_TEMPLATE_LOAD = 'BINDER_TEMPLATE_LOAD';
export const BINDER_TEMPLATE_LOAD_CLONE = 'BINDER_TEMPLATE_LOAD_CLONE';
export const BINDER_TEMPLATE_UNLOAD = 'BINDER_TEMPLATE_UNLOAD';
export const BINDER_TEMPLATE_RESET = 'BINDER_TEMPLATE_RESET';
export const BINDER_TEMPLATE_RESET_CLONE = 'BINDER_TEMPLATE_RESET_CLONE';
export const BINDER_TEMPLATE_UPDATE_PROP = 'BINDER_TEMPLATE_UPDATE_PROP';
export const BINDER_TEMPLATE_CHANGE_DOCUMENT_TYPE_LINK = 'BINDER_TEMPLATE_CHANGE_DOCUMENT_TYPE_LINK';
export const BINDER_TEMPLATE_ERRORS = 'BINDER_TEMPLATE_ERRORS';
export const BINDER_TEMPLATE_DELETE = 'BINDER_TEMPLATE_DELETE';
export const BINDER_TEMPLATE_SET_SELECTED_DOCUMENT_TYPE_ID = 'BINDER_TEMPLATE_SET_SELECTED_DOCUMENT_TYPE_ID';

export function loadBinderTemplateFromCsv({ institutionId, documentTypes, descriptors, dataSources, csv }) {
    return (dispatch) => {
        const { binderTemplate, errors } = new BinderTemplateCsvLoader({
            institutionId,
            documentTypes,
            descriptors,
            dataSources,
        }).load(csv);
        if (lodash.isEmpty(errors)) {
            dispatch(loadBinderTemplateAction(institutionId, binderTemplate, documentTypes, descriptors, dataSources));
            window.setTimeout(() =>
                dispatch(
                    addApplicationMessage('success', 'Binder Template loaded successfully from CSV file.', {
                        ttl: 2000,
                    })
                )
            );
        } else {
            dispatch(binderTemplateErrorsAction(null, errors, []));
        }
    };
}


export function initializeCreateBinderTemplate(institutionId, csv) {
    let query = toQuery([
        node('ComplionAdmin', [
            nodeWithArgs('institution', {
                id: institutionId,
            }, [
                node('dataSources', [
                    'id',
                    'name',
                    'description'
                ]),
                node('descriptors', [
                    'id',
                    'name',
                    'displayName',
                    'dataType',
                    'isActive',
                    node('dataset', [
                        'id',
                        'dataType',
                        'values',
                    ])
                ]),
                node('documentTypes', [
                    'id',
                    'name',
                    'displayName',
                    'isActive',
                    node('descriptors', [
                        'id',
                        'name',
                        'displayName',
                        'dataType',
                        'description',
                        'isActive',
                        'isActiveForDocumentType',
                        'isRequiredForDocumentType',
                        'displayOrder'
                    ])
                ]),
            ])
        ]),
    ]);

    return dispatch => {
        return queryGraphQL(query).then(response => {
            const {
                ComplionAdmin: {
                    institution: {
                        dataSources,
                        descriptors,
                        documentTypes,
                    },
                },
            } = response.data;
            dispatch(initializeCreateBinderTemplateAction(institutionId, documentTypes, descriptors, dataSources));
        });
    };
}

function createBinderTemplateLoader(action) {
    return function (institutionId, binderTemplateId) {
        const query = toQuery([
            node('ComplionAdmin', [
                nodeWithArgs('binderTemplate', {
                    id: binderTemplateId,
                    institutionId
                }, [
                    'id',
                    'displayName',
                    'description',
                    'status',
                    'binderType',
                    'billable',
                    'eventsTabEnabled',
                    node('documentTypes', [
                        'id',
                        'displayName'
                    ]),
                    node('queries', [
                        'id',
                        'displayName',
                        'description',
                        'query',
                        'binderTemplateId',
                        'parentId',
                        'displayOrder',
                        'options',
                        node('documentTypes', [
                            'id'
                        ]),
                    ])
                ]),
                nodeWithArgs('institution', {
                    id: institutionId,
                }, [
                    node('dataSources', [
                        'id',
                        'name',
                        'description',
                        'type'
                    ]),
                    node('descriptors', [
                        'id',
                        'name',
                        'displayName',
                        'dataType',
                        'isActive',
                        node('dataset', [
                            'id',
                            'dataType',
                            'values',
                        ])
                    ]),
                    node('documentTypes', [
                        'id',
                        'name',
                        'displayName',
                        'isActive',
                        node('descriptors', [
                            'id',
                            'name',
                            'displayName',
                            'dataType',
                            'description',
                            'isActive',
                            'isActiveForDocumentType',
                            'isRequiredForDocumentType',
                            'displayOrder'
                        ])
                    ]),
                ])
            ]),
        ]);

        return dispatch => {
            return queryGraphQL(query).then(response => {
                const {
                    ComplionAdmin: {
                        binderTemplate,
                        institution: {
                            dataSources,
                            descriptors,
                            documentTypes,
                        },
                    },
                } = response.data;
                dispatch(action(institutionId, binderTemplate, documentTypes, descriptors, dataSources));
            });
        };
    };
}

export const loadBinderTemplate = createBinderTemplateLoader(loadBinderTemplateAction);
export const loadBinderTemplateClone = createBinderTemplateLoader(loadBinderTemplateCloneAction);

export function unloadBinderTemplate() {
    return {
        type: BINDER_TEMPLATE_UNLOAD
    };
}

export function resetBinderTemplate(institutionId, binderTemplateId) {
    return {
        type: BINDER_TEMPLATE_RESET,
        payload: {
            institutionId,
            binderTemplateId
        }
    };
}

export function resetBinderTemplateClone(institutionId, binderTemplateId) {
    return {
        type: BINDER_TEMPLATE_RESET_CLONE,
        payload: {
            institutionId,
            binderTemplateId
        }
    };
}

export function updateBinderTemplateProp(binderTemplateId, propName, value) {
    return {
        type: BINDER_TEMPLATE_UPDATE_PROP,
        payload: {
            binderTemplateId,
            propName,
            value
        }
    };
}

export function changeDocumentTypeLink(institutionId, binderTemplateId, documentTypeId) {
    return {
        type: BINDER_TEMPLATE_CHANGE_DOCUMENT_TYPE_LINK,
        payload: {
            institutionId,
            binderTemplateId,
            documentTypeId
        }
    };
}

export function setSelectedDocumentTypeId(documentTypeId) {
    return {
        type: BINDER_TEMPLATE_SET_SELECTED_DOCUMENT_TYPE_ID,
        payload: {
            documentTypeId
        }
    };
}

function prepareBinderTemplate(binderTemplate, institutionId, storeState) {
    let preparedBinderTemplate = lodash.pick(binderTemplate, ['displayName', 'description', 'status', 'binderType', 'billable', 'eventsTabEnabled']);
    preparedBinderTemplate.institutionId = institutionId;
    preparedBinderTemplate.documentTypes = lodash.map(binderTemplate.documentTypes, 'id');
    preparedBinderTemplate = transformIntoEnumerations(preparedBinderTemplate, ['status']);
    preparedBinderTemplate = transformIntoEnumerations(preparedBinderTemplate, ['binderType']);

    const queryValidations = [];
    const nonQueryErrors = [];
    const { displayName, description } = preparedBinderTemplate;
    if (typeof displayName !== 'string' || displayName.length < 5 || displayName.length > 255) {
        nonQueryErrors.push('The display name must be a minimum of 5 characters and a maximum of 255 character');
    }
    if (typeof description !== 'string' || description.length < 5 || description.length > 500) {
        nonQueryErrors.push('The description must be a minimum of 5 characters and a maximum of 500 character');
    }

    function queryFilter(q) {
        return q.data.binderTemplateId === binderTemplate.id;
    }

    const preparedQueriesPromise = Promise.all(lodash.chain(storeState.queries)
        .filter(queryFilter)
        .map(q => {
            const data = { ...q.data };
            return buildQueryForSave(storeState, q.queryId).then(queryRebuild => {
                if (queryRebuild.validationErrors.hasErrors()) {
                    queryValidations.push(queryRebuild.validationErrors);
                }
                data.query = queryRebuild.queryString;
                data.options = JSON.stringify(data.options);
                delete data.childIds;
                return data;
            });
        }).value());

    return Promise.resolve(preparedQueriesPromise).then(preparedQueries => {
        preparedBinderTemplate.queries = preparedQueries;
        lodash.chain(storeState.queries).filter(queryFilter).value().forEach(q => validateFolder(storeState, q.queryId, queryValidations));

        if (queryValidations.length > 0 || nonQueryErrors.length > 0) {
            //will either have queryValidationErrors objects or simple plain objects from validation script
            const queryErrors = queryValidations.reduce((allErrors, queryValidation) =>
                allErrors.concat(lodash.isPlainObject(queryValidation)
                    ? queryValidation
                    : queryValidation.errors.map(e => e.getError())
                ), []);
            return {
                error: binderTemplateErrorsAction(binderTemplate.id, nonQueryErrors, queryErrors),
                preparedBinderTemplate: null
            };
        }

        return { preparedBinderTemplate: preparedBinderTemplate, error: null };
    });
}

export function createBinderTemplate(institutionId, binderTemplate) {
    return (dispatch, getState) => {
        dispatch(binderTemplateErrorsAction(binderTemplate.id, [], []));
        const storeState = getState();
        return Promise.resolve(prepareBinderTemplate(binderTemplate, institutionId, storeState)).then(preparedBinderTemplateResult => {
            if (preparedBinderTemplateResult.error) {
                dispatch(preparedBinderTemplateResult.error);
                dispatch(addApplicationMessage('error', 'The binder template has one or more errors'));
                return;
            }

            const mutationNode = nodeWithArgs('createBinderTemplate', {
                binderTemplate: preparedBinderTemplateResult.preparedBinderTemplate
            }, [
                'id'
            ]);

            const mutation = toMutation(mutationNode);
            return mutateGraphQL(mutation).then(results => {
                if (results.formattedErrors) {
                    dispatch(binderTemplateErrorsAction(binderTemplate.id, results.formattedErrors));
                    dispatch(addApplicationMessage('error', 'The binder template has one or more errors'));
                } else {
                    const id = results.data.createBinderTemplate.id;
                    const newLocation = `/complion/institutions/${institutionId}/binderTemplates/${id}/edit`;
                    dispatch(replace(newLocation));
                    dispatch(addApplicationMessage('success', 'Binder Template created successfully.', { ttl: 2000 }));
                }
            });
        });
    };
}

export function updateBinderTemplate(institutionId, binderTemplate) {
    return (dispatch, getState) => {
        dispatch(binderTemplateErrorsAction(binderTemplate.id, [], []));
        const storeState = getState();
        return Promise.resolve(prepareBinderTemplate(binderTemplate, institutionId, storeState)).then(preparedBinderTemplateResult => {
            if (preparedBinderTemplateResult.error) {
                dispatch(preparedBinderTemplateResult.error);
                dispatch(addApplicationMessage('error', 'The binder template has one or more errors'));
                return;
            }

            const mutationNode = nodeWithArgs('updateBinderTemplate', {
                id: binderTemplate.id,
                binderTemplate: preparedBinderTemplateResult.preparedBinderTemplate
            }, [
                'id'
            ]);

            const mutation = toMutation(mutationNode);
            return mutateGraphQL(mutation).then(results => {
                if (results.formattedErrors) {
                    dispatch(binderTemplateErrorsAction(binderTemplate.id, results.formattedErrors));
                    dispatch(addApplicationMessage('error', 'The binder template has one or more errors'));
                } else {
                    const id = results.data.updateBinderTemplate.id;
                    const newLocation = `/complion/institutions/${institutionId}/binderTemplates/${id}/edit`;
                    dispatch(replace(newLocation));
                    dispatch(addApplicationMessage('success', 'Binder Template updated successfully.', { ttl: 2000 }));
                }
            });
        });
    };
}

function initializeCreateBinderTemplateAction(institutionId, documentTypes, descriptors, dataSources) {
    return {
        type: BINDER_TEMPLATE_INITIALIZE,
        payload: {
            institutionId,
            documentTypes,
            descriptors,
            dataSources
        }
    };
}

function loadBinderTemplateAction(institutionId, binderTemplate, documentTypes, descriptors, dataSources) {
    return {
        type: BINDER_TEMPLATE_LOAD,
        payload: {
            institutionId,
            binderTemplate,
            documentTypes,
            descriptors,
            dataSources
        }
    };
}

function loadBinderTemplateCloneAction(institutionId, binderTemplate, documentTypes, descriptors, dataSources) {
    return {
        type: BINDER_TEMPLATE_LOAD_CLONE,
        payload: {
            institutionId,
            binderTemplate,
            documentTypes,
            descriptors,
            dataSources
        }
    };
}

export function binderTemplateErrorsAction(binderTemplateId, errors, queryErrors) {
    return {
        type: BINDER_TEMPLATE_ERRORS,
        payload: {
            binderTemplateId,
            errors,
            queryErrors,
        }
    };
}

export function validateFolder(storeState, queryId, queryErrors) {
    const query = storeState.queries[queryId];
    if (!query) {
        throw new Error(`Could not retrieve query with queryId = ${queryId}`);
    }
    const { data } = query;
    const { displayName, description, options } = data;
    if (typeof displayName !== 'string' || displayName.length < 2 || displayName.length > 80) {
        queryErrors.push({
            id: queryId,
            message: 'The display name must be a minimum of 2 characters and a maximum of 80 character',
            property: 'displayName',
            type: null
        });
    }
    if (typeof description !== 'string' || description.length < 2) {
        queryErrors.push({
            id: queryId,
            message: 'The description must be a minimum of 2 characters',
            property: 'description',
            type: null
        });
    }
    if (typeof options === 'object' && options !== null) {
        const { sort, customSort } = options;
        if (lodash.some(lodash.union(sort, customSort), sortObject => !Object.keys(ALLOWED_SORTING_TYPES).includes(sortObject.sortType))) {
            queryErrors.push({
                id: queryId,
                message: 'Please make sure all sorting types are filled in correctly.',
                property: 'options',
                type: null
            });
        }
        if (lodash.some(lodash.union(sort, customSort), sortObject => lodash.isNil(sortObject.subType))) {
            queryErrors.push({
                id: queryId,
                message: 'Please make sure all sorting sub types are filled in correctly.',
                property: 'options',
                type: null
            });
        }
        if (lodash.some(sort, sortObject => !Object.keys(ALLOWED_SORTING_DIRECTIONS).includes(sortObject.direction))) {
            queryErrors.push({
                id: queryId,
                message: 'Please make sure all sorting directions are filled in correctly.',
                property: 'options',
                type: null
            });
        }
    }
}

export function buildQueryForSave(storeState, queryId) {
    const rebuiltQuery = reassembleQuery(storeState.queries, storeState.nodes, storeState.valueSets, queryId);
    return validateQuery(rebuiltQuery).then(validationErrors => ({
        query: rebuiltQuery,
        validationErrors: validationErrors,
        queryString: jsonHelper.stringify(rebuiltQuery),
    }));
}

export function reassembleQuery(queries, nodes, valueSets, queryId) {
    const query = queries[queryId];
    if (!query) {
        throw new Error(`Could not retrieve query with queryId = ${queryId}`);
    }
    const rootNodeId = query.rootNodeId;
    return reassembleNode(nodes, valueSets, rootNodeId);
}

export function reassembleNode(nodes, valueSets, nodeId) {
    const node = nodes[nodeId];
    const reassembleData = lodash.clone(node.data);
    reassembleValueSets(valueSets, reassembleData);
    const QueryNodeType = QueryNodeRegistry.resolve(node.type);
    const reassembledChildNodes = node.childIds.map(childId => reassembleNode(nodes, valueSets, childId));
    return new QueryNodeType({ data: reassembleData, nodeId: nodeId }, ...reassembledChildNodes);
}

export function reassembleValueSets(valueSets, reassembleData) {
    for (let dataProp of Object.keys(reassembleData)) {
        const dataValue = reassembleData[dataProp];
        if (!dataValue) {
            continue;
        }
        if (dataValue._isValueSet && dataValue.valueSetId) {
            const valueSetInfo = valueSets[dataValue.valueSetId];
            const { type, data } = valueSetInfo;
            const ValueSetType = ValueSetRegistry.resolve(type);
            reassembleData[dataProp] = new ValueSetType(data);
            reassembleData[dataProp].valueSetId = dataValue.valueSetId;
        } else if (dataValue._isValueSet) {
            //should never happen
            throw new Error('Missing valueSetId');
        }
    }
}

export function deleteBinderTemplate(binderTemplateId, institutionId) {
    return dispatch => {

        const mutation = toMutation(
            nodeWithArgs('deleteBinderTemplate', {
                id: binderTemplateId
            })
        );

        return mutateGraphQL(mutation).then(results => {
            if (results.errors) {
                dispatch(addApplicationMessage('error', 'An error occurred when deleting the binder template.'));
            }
            dispatch({
                type: BINDER_TEMPLATE_DELETE,
                payload: {
                    deletedBinderTemplate: results.data.deleteBinderTemplate,
                    binderTemplateId,
                    institutionId
                }
            });
            dispatch(addApplicationMessage('success', 'Binder Template deleted successfully.'));
        });
    };
}
